<template>
  <div class="modal fade" id="highlight-detail-dialog" tabindex="-1" aria-labelledby="highlightDetailLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body pt-0">
          <div class="text-center">
            <div class="highlight-icon-wrapper mb-3">
              <img :src="iconUrl" :alt="highlight?.type" class="highlight-icon" />
            </div>
            
            <h4 class="mb-1">{{ highlight?.title || 'Custom POI' }}</h4>
            
            <p class="text-muted mb-3">
              <small>{{ prettyTypeName }}</small>
              <span v-if="highlight?.dayNumber" class="ms-2">• Day {{ highlight.dayNumber }}</span>
            </p>
            
            <div v-if="highlight?.description" class="mb-3 text-start">
              <p class="mb-1 highlight-description">{{ displayedDescription }}</p>
              <button
                v-if="descriptionNeedsTruncation"
                type="button"
                class="btn btn-link btn-sm p-0 see-more-toggle"
                @click="descriptionExpanded = !descriptionExpanded"
              >
                {{ descriptionExpanded ? 'See less' : 'See more' }}
              </button>
            </div>

            <!-- Audio -->
            <div v-if="hasAudio" class="audio-section mb-4 text-start">
              <div
                v-for="(audio, index) in audioAttachments"
                :key="audio.id || audio.clientId || index"
                class="audio-item mb-3"
              >
                <div class="d-flex align-items-center mb-1 text-muted">
                  <i class="bi bi-volume-up-fill me-2"></i>
                  <small>{{ audio.title || 'Audio' }}</small>
                </div>
                <audio
                  :src="audio.url"
                  controls
                  preload="none"
                  class="w-100"
                ></audio>
              </div>
            </div>

            <!-- Image Gallery -->
            <div v-if="hasImages" class="mb-4">
              <div v-if="images.length === 1" class="single-image-container">
                <img 
                  :src="images[0].file?.lgUrl || images[0].url" 
                  :alt="highlight?.title"
                  class="img-fluid rounded highlight-image"
                  @click="openLightbox(0)"
                  style="cursor: pointer; max-height: 400px; width: auto; margin: 0 auto;"
                />
              </div>
              <div v-else class="image-gallery">
                <div class="row g-2">
                  <div 
                    v-for="(image, index) in images.slice(0, 4)" 
                    :key="index"
                    :class="images.length === 2 ? 'col-6' : images.length === 3 && index === 0 ? 'col-12' : 'col-6'"
                  >
                    <div class="position-relative">
                      <img 
                        :src="image.file?.mdUrl || image.file?.lgUrl || image.url"
                        :alt="`${highlight?.title} - Image ${index + 1}`"
                        class="img-fluid rounded highlight-image"
                        @click="openLightbox(index)"
                        style="cursor: pointer; width: 100%; height: 200px; object-fit: cover;"
                      />
                      <div 
                        v-if="images.length > 4 && index === 3" 
                        class="overlay-more"
                        @click="openLightbox(3)"
                      >
                        <span class="text-white fs-4">+{{ images.length - 4 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { Modal } from 'bootstrap';
import { POIUtility } from '../../../utils/models/POI';
import { truncateAtWord, needsTruncation } from '../../../utils/helpers/truncateText';

// Highlight descriptions can run to several paragraphs (e.g. the imported
// Causeway Coast Way guide text). Collapse anything longer than this behind a
// "See more" toggle so the modal stays readable.
const DESCRIPTION_TRUNCATE_LIMIT = 300;

export default defineComponent({
  name: 'HighlightDetailDialog',
  setup() {
    const highlight = ref(null);
    const descriptionExpanded = ref(false);

    const descriptionNeedsTruncation = computed(() =>
      needsTruncation(highlight.value?.description, DESCRIPTION_TRUNCATE_LIMIT)
    );

    const displayedDescription = computed(() => {
      const description = highlight.value?.description || '';
      return descriptionExpanded.value
        ? description
        : truncateAtWord(description, DESCRIPTION_TRUNCATE_LIMIT);
    });

    const cleanupOrphanedBackdrops = () => {
      const openModals = document.querySelectorAll('.modal.show');
      const backdrops = document.querySelectorAll('.modal-backdrop');

      backdrops.forEach((backdrop, index) => {
        if (index >= openModals.length) {
          backdrop.remove();
        }
      });

      if (openModals.length === 0 && backdrops.length > 0) {
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
      }
    };
    
    const prettyTypeName = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        const poiUtil = POIUtility.fromString(highlight.value.type);
        return poiUtil.prettyName();
      } catch (e) {
        return highlight.value.type;
      }
    });
    
    const iconUrl = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        const poiUtil = POIUtility.fromString(highlight.value.type);
        return poiUtil.getPublicImageIcon();
      } catch (e) {
        // Return a default icon
        return POIUtility.fromString('other').getPublicImageIcon();
      }
    });
    
    const images = computed(() => {
      if (!highlight.value) return [];

      const existingImages = (highlight.value.images || []).filter(image => !image._destroy);
      const previewImages = (highlight.value.imagePreviewUrls || []).map(url => ({
        file: {
          lgUrl: url
        },
        url
      }));

      if (existingImages.length > 0 || previewImages.length > 0) {
        return [...existingImages, ...previewImages];
      }

      return [];
    });
    
    const hasImages = computed(() => images.value.length > 0);

    const audioAttachments = computed(() => {
      if (!highlight.value) return [];
      return (highlight.value.mediaAttachments || []).filter(
        media => media && media.mediaType === 'audio' && media.url
      );
    });

    const hasAudio = computed(() => audioAttachments.value.length > 0);

    const openLightbox = async (startIndex) => {
      // Wait for next tick to ensure DOM is updated
      await nextTick();
      
      // Create a temporary container for the lightbox
      const lightboxContainer = document.createElement('div');
      lightboxContainer.className = 'highlight-lightbox';
      lightboxContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      `;
      
      let currentIndex = startIndex;
      
      const updateImage = () => {
        const img = images.value[currentIndex];
        const imgUrl = img.file?.lgUrl || img.url;
        lightboxContainer.innerHTML = `
          <div style="position: relative; max-width: 90%; max-height: 90%;">
            <img src="${imgUrl}" style="max-width: 100%; max-height: 90vh; object-fit: contain;" />
            ${images.value.length > 1 ? `
              <button class="btn btn-light position-absolute" style="top: 50%; left: 10px; transform: translateY(-50%);">&lt;</button>
              <button class="btn btn-light position-absolute" style="top: 50%; right: 10px; transform: translateY(-50%);">&gt;</button>
              <div class="position-absolute text-white" style="bottom: 10px; left: 50%; transform: translateX(-50%);">
                ${currentIndex + 1} / ${images.value.length}
              </div>
            ` : ''}
            <button class="btn-close btn-close-white position-absolute" style="top: 10px; right: 10px;"></button>
          </div>
        `;
        
        // Add event listeners
        const closeBtn = lightboxContainer.querySelector('.btn-close');
        const prevBtn = lightboxContainer.querySelector('button:first-of-type');
        const nextBtn = lightboxContainer.querySelector('button:nth-of-type(2)');
        
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            document.body.removeChild(lightboxContainer);
          });
        }
        
        if (prevBtn && images.value.length > 1) {
          prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = (currentIndex - 1 + images.value.length) % images.value.length;
            updateImage();
          });
        }
        
        if (nextBtn && images.value.length > 1) {
          nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = (currentIndex + 1) % images.value.length;
            updateImage();
          });
        }
      };
      
      updateImage();
      
      // Close on background click
      lightboxContainer.addEventListener('click', (e) => {
        if (e.target === lightboxContainer) {
          document.body.removeChild(lightboxContainer);
        }
      });
      
      // Add keyboard navigation
      const handleKeydown = (e) => {
        if (e.key === 'Escape') {
          document.body.removeChild(lightboxContainer);
          document.removeEventListener('keydown', handleKeydown);
        } else if (e.key === 'ArrowLeft' && images.value.length > 1) {
          currentIndex = (currentIndex - 1 + images.value.length) % images.value.length;
          updateImage();
        } else if (e.key === 'ArrowRight' && images.value.length > 1) {
          currentIndex = (currentIndex + 1) % images.value.length;
          updateImage();
        }
      };
      
      document.addEventListener('keydown', handleKeydown);
      document.body.appendChild(lightboxContainer);
    };
    
    const showHighlight = (highlightData) => {
      cleanupOrphanedBackdrops();

      highlight.value = highlightData;
      descriptionExpanded.value = false;
      const modalElement = document.getElementById('highlight-detail-dialog');
      if (modalElement) {
        const existingModal = Modal.getInstance(modalElement);
        const modal = existingModal ?? new Modal(modalElement);

        const handleHidden = () => {
          modalElement.removeEventListener('hidden.bs.modal', handleHidden);
          cleanupOrphanedBackdrops();
        };

        modalElement.addEventListener('hidden.bs.modal', handleHidden, { once: true });
        modal.show();
      }
    };

    // Expose showHighlight method to be called from outside
    window.showHighlightDetail = showHighlight;

    onBeforeUnmount(() => {
      if (window.showHighlightDetail === showHighlight) {
        delete window.showHighlightDetail;
      }
      cleanupOrphanedBackdrops();
    });

    return {
      highlight,
      descriptionExpanded,
      descriptionNeedsTruncation,
      displayedDescription,
      prettyTypeName,
      iconUrl,
      images,
      hasImages,
      audioAttachments,
      hasAudio,
      openLightbox
    };
  }
});
</script>

<style scoped>
.highlight-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 50%;
}

.highlight-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.highlight-image {
  transition: transform 0.2s;
}

.highlight-image:hover {
  transform: scale(1.02);
}

.overlay-more {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.25rem;
}

.image-gallery {
  max-width: 500px;
  margin: 0 auto;
}

.single-image-container {
  text-align: center;
}

/* Preserve the paragraph breaks (\n) stored in the description instead of
   collapsing them the way default HTML white-space handling would. */
.highlight-description {
  white-space: pre-line;
}

.see-more-toggle {
  color: #FBCD01;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}

.see-more-toggle:hover {
  color: #FBCD01;
  text-decoration: underline;
}
</style>
